import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  LetterTile,
  PopIn,
  RoundCounter,
  ShakeWrap,
  Tile,
  gameShell,
  itemKey,
  pieceButtonProps,
  seededShuffle,
  useCompleteOnce,
  useDragAndTap,
  useLetterAccent,
  useWrongFlash,
  zoneStyle,
} from "./engineUtils";

/** Where the letter sound sits, from the many spellings the Swift banks use. */
export function positionFromSound(startingSound, item) {
  const s = String(startingSound || "").toLowerCase();
  if (!s) return item?.isTarget === false ? "none" : "beginning";
  if (/inside|medial|middle/.test(s)) return "middle";
  if (/^(no|none|other|absent)/.test(s) || /other|absent/.test(s)) return "none";
  if (/final|^end|ending/.test(s)) return "end";
  if (/^initial|begin|start|^short|^long/.test(s)) return "beginning";
  return "beginning";
}

const POSITIONS = [
  { id: "beginning", label: "Beginning", accent: LEARN.color.mint, deep: LEARN.color.mintDeep },
  { id: "middle", label: "Middle", accent: LEARN.color.sun, deep: LEARN.color.sunDeep },
  { id: "end", label: "End", accent: LEARN.color.lavender, deep: LEARN.color.lavenderDeep },
  { id: "none", label: "Not here", accent: LEARN.color.coral, deep: LEARN.color.coralDeep },
];

/**
 * Sound position: hear a word, then drag the letter token to where its sound
 * lives (beginning / middle / end / not here) — or tap the spot. Completes
 * after roundSize (default 6) words.
 */
export default function SoundPositionGame({ letter, config, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { wrongId, flash } = useWrongFlash();
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const { accent: letterAccent } = useLetterAccent(letter);
  const L = String(config.letter || letter || "").toUpperCase();
  const items = useMemo(
    () => seededShuffle(config.items || [], 44).slice(0, config.roundSize || 6),
    [config.items, config.roundSize]
  );
  const positions = useMemo(() => {
    const used = new Set(items.map((it) => positionFromSound(it.startingSound, it)));
    return POSITIONS.filter((p) => p.id === "beginning" || p.id === "end" || used.has(p.id));
  }, [items]);

  const [idx, setIdx] = useState(0);
  const [solved, setSolved] = useState(null); // position id once answered
  const [burst, setBurst] = useState(0);
  const timers = useRef([]);
  const current = items[idx];
  const answer = current ? positionFromSound(current.startingSound, current) : null;

  const later = useCallback((fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); return t; }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (!current) return;
    setSolved(null);
    later(() => speak?.(current.word), idx === 0 ? 900 : 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const choose = useCallback(
    (posId) => {
      if (!current || solved) return false;
      if (posId !== answer) {
        flash(itemKey(current));
        onWrong?.();
        return false;
      }
      sfx?.("snap");
      setSolved(posId);
      later(() => { sfx?.("correct", { volume: 0.7 }); speak?.(current.word, { interrupt: true }); setBurst((b) => b + 1); }, 150);
      later(() => {
        if (idx + 1 >= items.length) finish();
        else setIdx((x) => x + 1);
      }, reduceMotion ? 400 : 1300);
      return true;
    },
    [answer, current, finish, flash, idx, items.length, later, onWrong, reduceMotion, sfx, solved, speak]
  );

  const onDrop = useCallback(
    ({ payload, targetId }) => {
      if (!targetId || !payload) return false;
      return choose(targetId);
    },
    [choose]
  );
  const drag = useDragAndTap({ reduceMotion, sfx, onDrop });

  if (!current) return <div style={gameShell.root} />;

  const token = (extra = {}) => (
    <LetterTile text={L} scale={compact ? 1 : 1.15} {...extra} />
  );

  return (
    <div style={{ ...gameShell.root, gap: compact ? 10 : 16 }}>
      <ConfettiBurst key={burst} active={burst > 0} originY={35} count={22} reduceMotion={reduceMotion} />
      <RoundCounter done={idx} total={items.length} accent={letterAccent} />
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: compact ? 12 : 20, paddingTop: compact ? 30 : 0 }}>
        {/* Picture + word */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={itemKey(current)}
            initial={reduceMotion ? false : { scale: 0.6, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { scale: 0.7, opacity: 0, x: -60, transition: { duration: 0.18 } }}
            transition={{ type: "spring", stiffness: 340, damping: 22 }}
            style={{ display: "flex", alignItems: "center", gap: compact ? 12 : 20 }}
          >
            <ShakeWrap shake={wrongId === itemKey(current)} reduceMotion={reduceMotion} style={{ borderRadius: "calc(var(--tile) * 0.42)" }}>
              <button
                type="button"
                className="learn-piece"
                aria-label={`Hear ${current.word}`}
                onClick={() => { sfx?.("pop", { volume: 0.5 }); speak?.(current.word, { interrupt: true }); }}
                style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", borderRadius: "calc(var(--tile) * 0.42)" }}
              >
                <Tile letter={letter} assetName={current.assetName} alt={current.word} scale={compact ? 1.6 : 1.9} lift={false} solved={Boolean(solved)} />
              </button>
            </ShakeWrap>
            <WordStrip word={current.word} letter={L} solved={solved} compact={compact} reduceMotion={reduceMotion} />
          </motion.div>
        </AnimatePresence>

        {/* Letter token (drag it) */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            {...pieceButtonProps({
              dragProps: solved ? null : drag.dragProps,
              payload: { letter: L },
              render: () => token(),
              label: `Letter ${L}`,
              disabled: Boolean(solved),
              onTap: () => { sfx?.("pop", { volume: 0.5 }); speak?.(L, { interrupt: true }); drag.pick("token", { letter: L }); },
            })}
            aria-pressed={drag.isPicked("token")}
          >
            {token({ selected: drag.isPicked("token"), dim: Boolean(solved) })}
          </button>
        </div>

        {/* Position zones */}
        <div style={{ display: "flex", gap: compact ? 8 : 12, justifyContent: "center", flexWrap: "wrap", width: "100%" }}>
          {positions.map((p, i) => {
            const hover = drag.hoverId === p.id;
            const isSolved = solved === p.id;
            const armed = Boolean(drag.picked) && !solved;
            return (
              <PopIn key={p.id} index={i + 1} reduceMotion={reduceMotion} style={{ flex: `1 1 calc(var(--tile) * 1.6)`, maxWidth: "calc(var(--tile) * 2.6)" }}>
                <div
                  ref={(el) => drag.registerTarget(p.id, el, { position: p.id })}
                  role="button"
                  tabIndex={0}
                  aria-label={p.label}
                  aria-pressed={isSolved}
                  onClick={() => { if (solved) return; if (drag.picked) drag.tapTarget(p.id, { position: p.id }); else { sfx?.("pop", { volume: 0.4 }); choose(p.id); } }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!solved) choose(p.id); } }}
                  className={solved ? undefined : "learn-slot-armed"}
                  style={{
                    ...zoneStyle({ accent: p.accent, hover, filled: isSolved }),
                    "--accent": p.accent, "--accent-soft": `${p.accent}22`, "--accent-ring": `${p.accent}33`,
                    width: "100%",
                    minHeight: "calc(var(--tile) * 1.35)",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
                    padding: "12px 8px",
                    cursor: solved ? "default" : "pointer",
                    borderStyle: armed || isSolved ? "solid" : undefined,
                    background: isSolved ? `${p.accent}33` : undefined,
                    outline: "none",
                  }}
                >
                  <PositionGlyph id={p.id} accent={p.accent} letter={L} filled={isSolved} />
                  <span style={{ fontFamily: LEARN.font, fontWeight: 700, fontSize: compact ? 16 : 18, color: isSolved ? p.deep : LEARN.color.ink, transition: "color 200ms ease" }}>{p.label}</span>
                </div>
              </PopIn>
            );
          })}
        </div>
      </div>
      <drag.GhostLayer />
    </div>
  );
}

/** Three-bead word shape with the letter sitting where the sound lives. */
function PositionGlyph({ id, accent, letter, filled }) {
  const beads = [0, 1, 2];
  const hot = id === "beginning" ? 0 : id === "middle" ? 1 : id === "end" ? 2 : -1;
  return (
    <div aria-hidden="true" style={{ display: "flex", gap: 5, alignItems: "center" }}>
      {beads.map((b) => (
        <span
          key={b}
          style={{
            width: b === hot ? 30 : 22, height: b === hot ? 30 : 22, borderRadius: 9,
            background: b === hot ? accent : "rgba(11,26,51,0.12)",
            display: "grid", placeItems: "center", color: "#fff", fontFamily: LEARN.font, fontWeight: 700, fontSize: 17,
            boxShadow: b === hot && filled ? `0 0 0 3px ${accent}55` : "none",
            transition: "transform 160ms ease",
            transform: b === hot && filled ? "scale(1.12)" : "none",
          }}
        >
          {b === hot && filled ? letter : ""}
        </span>
      ))}
      {hot === -1 && (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="3.2" strokeLinecap="round" style={{ marginLeft: 2 }}>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      )}
    </div>
  );
}

/** The word, big, with the letter's spot lighting up once answered. */
function WordStrip({ word, letter, solved, compact, reduceMotion }) {
  const chars = String(word).split("");
  const l = letter.toLowerCase();
  const positions = chars.map((ch) => ch.toLowerCase() === l);
  const first = positions.indexOf(true);
  const last = positions.lastIndexOf(true);
  const hot = (i) => {
    if (!solved || solved === "none") return false;
    if (solved === "beginning") return i === first && first === 0;
    if (solved === "end") return i === last && last === chars.length - 1;
    return positions[i] && i > 0 && i < chars.length - 1;
  };
  return (
    <div
      style={{
        display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center", maxWidth: "calc(var(--arena-w) - var(--tile) * 2.6)",
        padding: compact ? "8px 12px" : "12px 18px",
        background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        borderRadius: LEARN.radius.lg, boxShadow: LEARN.shadow.card, border: `2px solid ${LEARN.color.line}`,
        fontFamily: LEARN.font, fontWeight: 700, fontSize: compact ? 24 : 32, color: LEARN.color.ink, lineHeight: 1,
      }}
      aria-label={word}
    >
      {chars.map((ch, i) => (
        <motion.span
          key={i}
          animate={hot(i) && !reduceMotion ? { scale: [1, 1.3, 1.15], y: [0, -6, -2] } : { scale: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            display: "inline-block", minWidth: "0.7em", textAlign: "center", padding: "2px 3px", borderRadius: 8,
            color: hot(i) ? "#fff" : LEARN.color.ink, background: hot(i) ? LEARN.color.blue : "transparent",
            transition: "background 200ms ease, color 200ms ease",
          }}
        >
          {ch}
        </motion.span>
      ))}
    </div>
  );
}
